import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { z } from 'zod';

type ZodPrimitive = z.ZodType<string | number | Date>;
type ZodPrimitiveArray = z.ZodArray<ZodPrimitive>;

type SchemaType = ZodPrimitive | ZodPrimitiveArray;

type Parser<T extends SchemaType> = {
    schema: T;
};

type ParserWithDefault<T extends SchemaType> = Parser<T> & {
    defaultValue: z.infer<T>;
};

const parseData = <T extends SchemaType>(data: unknown, schema: T): z.infer<T> | null => {
    const parsed = schema.safeParse(data);
    if (parsed.success) {
        return parsed.data as z.output<T>;
    }
    return null;
};

function buildSearchParams<T extends SchemaType>(prev: URLSearchParams, newValue: z.infer<T> | null, key: string) {
    const newParams = new URLSearchParams(prev);
    if (newValue === null) {
        newParams.delete(key);
        return newParams;
    }
    if (Array.isArray(newValue)) {
        newParams.delete(key);
        newValue.forEach(v => {
            newParams.append(key, v.toString());
        });
    } else {
        newParams.set(key, newValue.toString());
    }
    return newParams;
}

export function useSearchParam<T extends SchemaType>(
    key: string,
    schema: ParserWithDefault<T>
): [z.infer<T>, (value: z.infer<T> | null) => void, () => void];

export function useSearchParam<T extends SchemaType>(
    key: string,
    schema: Parser<T>
): [z.infer<T> | null, (value: z.infer<T> | null) => void, () => void];

export function useSearchParam<T extends SchemaType>(key: string, parser: Parser<T> & { defaultValue?: z.infer<T> }) {
    const [searchParams, setSearchParams] = useSearchParams();

    const { schema, defaultValue = null } = parser;
    const value = useMemo(() => {
        const isArray = schema instanceof z.ZodArray;

        // If expecting array and key not present at all -> treat as null so defaultValue can be used
        if (isArray && !searchParams.has(key)) {
            return null;
        }

        const param = isArray ? searchParams.getAll(key) : searchParams.get(key);

        return parseData(param, schema);
    }, [schema, searchParams, key]);

    const setValue = useCallback(
        (newValue: z.infer<T> | null) => {
            setSearchParams(prev => buildSearchParams(prev, newValue, key));
        },
        [key, setSearchParams]
    );

    const deleteValues = useCallback(() => {
        setSearchParams(params => {
            params.delete(key);
            return params;
        });
    }, [key, setSearchParams]);

    return [value ?? defaultValue, setValue, deleteValues];
}

type ParserBuilder<T extends SchemaType> = Parser<T> & {
    default: (defaultValue: z.infer<T>) => Omit<ParserWithDefault<T>, 'defaultValue'> & {
        defaultValue: NonNullable<z.infer<T>>;
    };
};

const buildParser = <T extends ZodPrimitive>(schema: T): ParserBuilder<T> => ({
    schema,
    default: (defaultValue: z.infer<T>): ParserWithDefault<T> => ({
        schema,
        defaultValue,
    }),
});

export const parseAsArrayOf = <T extends ZodPrimitive>(
    parserBuilder: ParserBuilder<T>
): ParserBuilder<z.ZodArray<T>> => {
    const arraySchema = parserBuilder.schema.array() as z.ZodArray<T>;
    return {
        schema: arraySchema,
        default: (defaultValue: z.infer<z.ZodArray<T>>): ParserWithDefault<z.ZodArray<T>> => ({
            schema: arraySchema,
            defaultValue,
        }),
    };
};

export const stringParser = buildParser(z.string());
export const numberParser = buildParser(z.coerce.number());
export const dateParser = buildParser(z.coerce.date());
